import React, { useEffect, useMemo, useState } from 'react'
import {
  ListBox,
  ListBoxItem,
  ListLayout,
  Virtualizer
} from 'react-aria-components'

import classnames from 'classnames'

import ToggleInput from 'src/shared/components/common/forms/ToggleInput'
import { t } from 'src/translations'

import { useA11y } from 'public/components/online_ordering/useA11y'
import { SearchBox } from 'public/components/default_template/search'

// Above this many values a section gains a search box and virtualized scroll.
export const SEARCHABLE_THRESHOLD = 8

export type FilterOption = { id?: string; name: string }

const filterOptionKey = (option: FilterOption) => option.id ?? option.name

// Maps Enter/Space to a click for the bare-checkbox rows; the virtualized branch defers to react-aria's ListBox keyboard handling instead.
const toggleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    event.currentTarget.click()
  }
}

export type FilterOptionRowsProps = {
  // Section label used for the search/list a11y text.
  sectionName: string
  // Fully-composed prefix for each row's name/id (e.g. 'filter-brand'). the row id is `${name}-${key}`.
  name: string
  values: FilterOption[]
  selectedValues: Set<string>
  onToggle: (key: string) => void
  // Gates the virtualizer until the container is open/measurable — rAF-driven in the bar dropdown, enteredSection-driven in the panel.
  hasEntered: boolean
}

export const FilterOptionRows = ({
  sectionName,
  name,
  values,
  selectedValues,
  onToggle,
  hasEntered
}: FilterOptionRowsProps) => {
  const [search, setSearch] = useState('')
  const { announce } = useA11y()

  const searchLabel = t('filters.search-in-section', { section: sectionName })
  const listLabel = t('filters.options-list', { section: sectionName })

  const filteredValues = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) {
      return values
    }
    return values.filter((value) => value.name.toLowerCase().includes(query))
  }, [values, search])

  // Announce the filtered result count to screen readers. Only while a query is active, so opening/clearing the section stays silent.
  useEffect(() => {
    if (!search.trim()) {
      return
    }
    const message =
      filteredValues.length > 0
        ? t('sr-only.results-found', { count: filteredValues.length })
        : t('sr-only.no-results-found')
    announce(message)
  }, [search, filteredValues.length, announce])

  if (values.length <= SEARCHABLE_THRESHOLD) {
    return (
      <div className='searchableScrollableOptions'>
        {values.map((value) => {
          const key = filterOptionKey(value)
          return (
            <ToggleInput
              key={key}
              type='checkbox'
              name={name}
              id={`${name}-${key}`}
              checked={selectedValues.has(key)}
              onChange={() => onToggle(key)}
              onKeyDown={toggleOnKeyDown}
              additionalClassNames='filterOption'
            >
              {value.name}
            </ToggleInput>
          )
        })}
      </div>
    )
  }

  return (
    <div className='searchableScrollableOptions'>
      <SearchBox
        className='filterOptionSearch ooSearch'
        inputClassName='searchBox'
        id={`${name}-search`}
        dataTestId={`${name}-search`}
        value={search}
        onValueChange={setSearch}
        placeholder={searchLabel}
        ariaLabel={searchLabel}
      />
      {/* Defer the virtualizer until the section has finished opening, so its
          ResizeObserver never measures a collapsed or mid-animation container. */}
      {hasEntered && (
        <Virtualizer layout={ListLayout}>
          <ListBox
            className='scrollableOptions'
            aria-label={listLabel}
            selectionMode='multiple'
            selectedKeys={selectedValues}
            onSelectionChange={(keys) => {
              // Ignore cmd/ctrl+A
              if (keys === 'all') return
              // Range selection (shift-click) can't happen here: we pass a fresh Set as selectedKeys each render, which RAC can't extend a range from — so every interaction changes exactly one key.
              filteredValues.forEach((value) => {
                const key = filterOptionKey(value)
                if (keys.has(key) !== selectedValues.has(key)) {
                  onToggle(key)
                }
              })
            }}
            items={filteredValues.map((value) => ({
              ...value,
              key: filterOptionKey(value)
            }))}
            renderEmptyState={() => (
              <p className='noResults'>{t('filters.no-matches')}</p>
            )}
          >
            {(item) => (
              <ListBoxItem
                key={item.key}
                id={item.key}
                textValue={item.name}
                // Flag the last row to drop its separator; CSS :last-child would target the last mounted row, not the actual last value.
                className={classnames('listBoxOption', {
                  lastOption:
                    item.key ===
                    filterOptionKey(filteredValues[filteredValues.length - 1]!)
                })}
              >
                {({ isSelected }) => (
                  // the ListBoxItem owns focus/keyboard/selection, so the input is out of the tab order and its clicks pass through (see .listBoxOption in SCSS).
                  <ToggleInput
                    type='checkbox'
                    name={name}
                    id={`${name}-${item.key}`}
                    checked={isSelected}
                    onChange={() => {}}
                    tabIndex={-1}
                    ariaHidden
                    additionalClassNames='filterOption'
                  >
                    {item.name}
                  </ToggleInput>
                )}
              </ListBoxItem>
            )}
          </ListBox>
        </Virtualizer>
      )}
    </div>
  )
}
